import tailwindColors from "tailwindcss/colors";

import { toHex, withCssAlpha } from "~/utils/color";

const darkSurface = tailwindColors.zinc;

const makeVerticalGradient = (from: string, to: string, fallback = from) =>
  `linear-gradient(180deg, ${from} 0%, ${to} 100%), ${fallback}`;

export const colorsByTheme = {
  light: {
    bgStd: tailwindColors.white,
    bgLt: tailwindColors.gray["100"],
    bgMd: tailwindColors.gray["200"],
    bgHvy: tailwindColors.gray["300"],
    borderVlt: tailwindColors.gray["100"],
    borderLt: tailwindColors.gray["200"],
    borderMd: tailwindColors.gray["300"],
    borderHvy: tailwindColors.gray["500"],
    textVlt: tailwindColors.gray["400"],
    textLt: tailwindColors.gray["500"],
    textMd: tailwindColors.gray["700"],
    textHvy: tailwindColors.gray["900"],
    // TODO look more carefully at anything below this
    highlight: tailwindColors.gray["200"],
    highlightLt: tailwindColors.gray["100"],
    btn: tailwindColors.gray["300"],
    btnHover: tailwindColors.gray["400"],
    btnHvy: tailwindColors.gray["400"],
    btnHvyHover: tailwindColors.gray["500"],
    btnVlt: tailwindColors.gray["200"],
    btnVltHover: tailwindColors.gray["300"],
    actionBtn: tailwindColors.indigo["500"],
    rowSoftSelection: tailwindColors.gray["100"],
    rowHardSelection: withCssAlpha(tailwindColors.indigo["500"], 0.15),
    rowBothSelection: withCssAlpha(tailwindColors.indigo["500"], 0.25),
    miniRowHardSelection: tailwindColors.gray["100"],
    miniRowBothSelection: tailwindColors.gray["200"],
    opposite: tailwindColors.gray["900"],
    primary: tailwindColors.indigo["500"],
    primaryHover: tailwindColors.indigo["600"],
    primaryHighContrast: tailwindColors.indigo["800"],
    success: tailwindColors.green["500"],
    warning: tailwindColors.orange["500"],
    danger: tailwindColors.red["500"],
    dangerHover: tailwindColors.red["600"],
    dangerHighContrast: tailwindColors.red["800"],
    recommendation: tailwindColors.purple["500"],
    recommendationHighContrast: tailwindColors.purple["800"],
  },
  dark: {
    bgStd: darkSurface["900"],
    bgLt: darkSurface["800"],
    bgMd: darkSurface["700"],
    bgHvy: darkSurface["600"],
    borderVlt: darkSurface["800"],
    borderLt: darkSurface["700"],
    borderMd: darkSurface["600"],
    borderHvy: darkSurface["400"],
    textVlt: darkSurface["500"],
    textLt: darkSurface["400"],
    textMd: darkSurface["300"],
    textHvy: darkSurface["50"],
    // TODO see above
    highlight: darkSurface["700"],
    highlightLt: darkSurface["800"],
    button: darkSurface["600"],
    btnHover: darkSurface["500"],
    btnHvy: darkSurface["500"],
    btnHvyHover: darkSurface["400"],
    btnLtHover: darkSurface["700"],
    btnVltHover: darkSurface["600"],
    actionBtn: tailwindColors.indigo["500"],
    rowSoftSelection: darkSurface["800"],
    rowHardSelection: withCssAlpha(tailwindColors.indigo["500"], 0.15),
    rowBothSelection: withCssAlpha(tailwindColors.indigo["500"], 0.25),
    miniRowHardSelection: darkSurface["800"],
    miniRowBothSelection: darkSurface["700"],
    opposite: darkSurface["50"],
    primary: tailwindColors.indigo["500"],
    primaryHover: tailwindColors.indigo["400"],
    primaryHighContrast: tailwindColors.indigo["200"],
    success: tailwindColors.green["500"],
    warning: tailwindColors.orange["500"],
    danger: tailwindColors.red["500"],
    dangerHover: tailwindColors.red["400"],
    dangerHighContrast: tailwindColors.red["200"],
    recommendation: tailwindColors.purple["500"],
    recommendationHighContrast: tailwindColors.purple["200"],
  },
};

type ChartColorSource = Pick<
  (typeof colorsByTheme)["light"],
  | "bgStd"
  | "bgMd"
  | "borderLt"
  | "borderMd"
  | "textHvy"
  | "textLt"
  | "textMd"
  | "opposite"
  | "primary"
  | "success"
  | "warning"
  | "recommendation"
>;

const makeChartColors = (colors: ChartColorSource) => ({
  bgStd: toHex(colors.bgStd),
  bgMd: toHex(colors.bgMd),
  borderLt: toHex(colors.borderLt),
  borderMd: toHex(colors.borderMd),
  textHvy: toHex(colors.textHvy),
  textLt: toHex(colors.textLt),
  textMd: toHex(colors.textMd),
  opposite: toHex(colors.opposite),
  primary: toHex(colors.primary),
  success: toHex(colors.success),
  warning: toHex(colors.warning),
  recommendation: toHex(colors.recommendation),
});

export const chartColorsByTheme = {
  light: makeChartColors(colorsByTheme.light),
  dark: makeChartColors(colorsByTheme.dark),
};

export const DART_ICON_GRADIENT_BY_THEME = {
  light: makeVerticalGradient(tailwindColors.white, tailwindColors.gray["300"]),
  dark: makeVerticalGradient(darkSurface["900"], darkSurface["700"]),
};

export const EMPTY_STATE_ICON_GRADIENT_BY_THEME = {
  light: DART_ICON_GRADIENT_BY_THEME.light,
  dark: makeVerticalGradient(darkSurface["900"], darkSurface["600"]),
};

export const DEFAULT_CHIP_COLOR = toHex(tailwindColors.gray["200"]);
export const DEFAULT_BLOCK_COLOR = toHex(tailwindColors.gray["400"]);
export const PRIMARY_COLOR = toHex(tailwindColors.indigo["500"]);
export const RECOMMENDATION_COLOR = toHex(tailwindColors.purple["500"]);
export const BACKUP_FONTS = [
  "ui-sans-serif",
  "system-ui",
  "sans-serif",
  "Apple Color Emoji",
  "Segoe UI Emoji",
  "Segoe UI Symbol",
  "Noto Color Emoji",
].join(", ");
